<script lang="ts">
  import { xhr, toast } from "$app/utils"
  import ImageCropper from "$app/components/ImageCropper.svelte"
  import Loading from "$app/components/Loading.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { monthNames } from "$app/constants"
  import { appStore } from "$app/store.svelte"

  interface Props {
    market_segments: { id: number; name: string }[]
    logo_name?: string
    time_zones: any[]
  }

  let { market_segments, logo_name = "", time_zones }: Props = $props()

  let saving = $state(false)
  let changedLogo = $state(false)
  let openImgCropper = $state(false)
  let fileImage: any = $state()
  let fileInput: HTMLInputElement = $state()
  let logoTmp: any = $state()

  if (logo_name) {
    const name = logo_name.split("/").pop().split("#")[0].split("?")[0]
    if (name.match(/\.(jpg|jpeg)$/i)) {
      fileImage = { type: "image/jpeg", name }
    } else {
      fileImage = { type: "image/png", name }
    }
  }

  let form = $state({
    name: appStore.plan.name,
    company_name: appStore.plan.company_name,
    affected: appStore.plan.affected,
    zipcode: appStore.plan.zipcode,
    market_id: appStore.plan.market_id,
    nonprofit: appStore.plan.nonprofit,
    time_zone: appStore.plan.time_zone,
    fiscal_year_start_month: appStore.plan.fiscal_year_start_month,
    community_read_only: appStore.plan.community_read_only
  })
  let errors: any = $state({})

  async function savePlan(): Promise<void> {
    saving = true
    errors = {}
    const formData = new FormData()
    Object.keys(form).forEach((fld) => {
      formData.append(`plan[${fld}]`, form[fld])
    })
    if (changedLogo) {
      formData.append("plan[logo]", fileImage)
    }

    try {
      const resp = await xhr.patch<PlanType>(
        `/plans/${appStore.plan.id}.json`,
        formData
      )

      appStore.plan = { ...appStore.plan, ...resp.data.plan }
      toast("The Plan has been updated", "success")
    } catch (e) {
      if (e.response?.data) {
        errors = e.response.data
      } else {
        Honeybadger.notify(e)
      }
      toast("There was an error saving your plan data", "error")
    } finally {
      saving = false
    }
  }

  function onFileSelected(target: HTMLInputElement): void {
    fileImage = target.files[0]
    let reader = new FileReader()
    reader.readAsDataURL(fileImage)
    reader.onload = (e) => {
      changedLogo = true
      logoTmp = e.target.result
      openImgCropper = true
    }
  }
</script>

<form
  onsubmit={(e) => {
    e.preventDefault()
    savePlan()
  }}
  class="space-y-8 max-w-3xl mx-auto"
>
  <div class="w-full flex justify-center">
    <div class="space-y-1 text-center">
      <div class="mx-auto h-24 w-24 rounded-full bg-gray-100">
        <img
          data-name="plan-logo"
          src={appStore.plan.logo_url}
          class="p-1 w-full h-full bg-gray-100 rounded-full shadow flex-shrink-0"
          alt="Logo"
        />
      </div>
      <div class="flex text-sm text-black justify-center pt-2">
        <button
          onclick={() => {
            fileInput.click()
          }}
          class="relative cursor-pointer bg-white rounded-md font-medium text-black hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
        >
          <span>Replace Logo</span>
          <input
            type="file"
            class="hidden"
            name="plan-logo-img"
            bind:this={fileInput}
            onchange={(e) => onFileSelected(e.target as HTMLInputElement)}
          />
        </button>
      </div>
      {#if changedLogo}
        <ImageCropper
          image={logoTmp}
          open={openImgCropper}
          file={fileImage}
          cropShape="round"
          cropperClose={() => {
            openImgCropper = false
          }}
          imageCropped={(data) => {
            appStore.plan.logo_url = data.image
            changedLogo = true
            fileImage = data.file
            savePlan()
          }}
        />
      {/if}

      <p class="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
    </div>
  </div>
  <div class="grid grid-cols-6 gap-6">
    <div class="col-span-6 md:col-span-3 input-cont" class:error={errors.name}>
      <label for="plan[name]" class="block text-sm font-medium text-gray-700"
        >Plan Name</label
      >
      <input
        type="text"
        bind:value={form.name}
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      />
      <div class="msg">{errors.name}</div>
    </div>

    <div
      class="col-span-6 md:col-span-3 input-cont"
      class:error={errors.company_name}
    >
      <label
        for="plan[company_name]"
        class="block text-sm font-medium text-gray-700">Company Name</label
      >
      <input
        type="text"
        bind:value={form.company_name}
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      />
      <div class="msg">{errors.company_name}</div>
    </div>

    <div
      class="col-span-6 md:col-span-3 input-cont"
      class:error={errors.affected}
    >
      <label
        for="plan[affected]"
        class="block text-sm font-medium text-gray-700"
        >Number of Employees</label
      >
      <input
        type="number"
        bind:value={form.affected}
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      />
      <div class="msg">{errors.affected}</div>
    </div>

    <div
      class="col-span-6 md:col-span-3 input-cont"
      class:error={errors.zipcode}
    >
      <label for="plan[zipcode]" class="block text-sm font-medium text-gray-700"
        >Zip Code</label
      >
      <input
        type="text"
        bind:value={form.zipcode}
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      />
      <div class="msg">{errors.zipcode}</div>
    </div>

    <div class="col-span-6 md:col-span-3">
      <label
        for="plan[nonprofit]"
        class="block text-sm font-medium text-gray-700"
        >For Profit / Non-profit</label
      >
      <select
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-blue-500 sm:text-sm"
        bind:value={form.nonprofit}
      >
        {#each [{ id: false, name: "For Profit" }, { id: true, name: "Non-Profit" }] as ms (ms.id)}
          <option value={ms.id}>{ms.name}</option>
        {/each}
      </select>
    </div>

    <div class="col-span-6 md:col-span-3">
      <label
        for="plan[market_id]"
        class="block text-sm font-medium text-gray-700">Industry</label
      >
      <select
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        bind:value={form.market_id}
      >
        {#each market_segments as ms (ms.id)}
          <option value={ms.id}>{ms.name}</option>
        {/each}
      </select>
    </div>

    <div class="col-span-6 md:col-span-3">
      <label
        for="plan[time_zone]"
        class="block text-sm font-medium text-gray-700"
      >
        Time Zone
        <span class="text-xs"> (Used for email notification and reports) </span>
      </label>
      <select
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        bind:value={form.time_zone}
      >
        {#each time_zones as tz (tz.name)}
          <option value={tz.name}>{tz.name}</option>
        {/each}
      </select>
    </div>

    <div class="col-span-6 md:col-span-3">
      <label
        for="plan[fiscal_year_start_month]"
        class="block text-sm font-medium text-gray-700"
      >
        Fiscal Year Start Month
        <span class="text-xs"> (The month your fiscal Q1 begins) </span>
      </label>
      <select
        name="plan[fiscal_year_start_month]"
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        bind:value={form.fiscal_year_start_month}
      >
        {#each monthNames as month, i}
          <option value={i + 1}>{month}</option>
        {/each}
      </select>
    </div>

    <div class="col-span-6 md:col-span-3">
      <label
        for="plan[nonprofit]"
        class="block text-sm font-medium text-gray-700"
        >Community Member Access</label
      >
      <select
        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-blue-500 sm:text-sm"
        bind:value={form.community_read_only}
      >
        <option value={false}>Members can Post and Comment</option>
        <option value={true}>Read-only</option>
      </select>
    </div>

    <div class="col-span-6 py-4 text-center">
      <Loading loading={saving}>
        <button
          type="submit"
          disabled={saving}
          class="inline-flex justify-center py-3 px-12 border border-transparent shadow-sm font-medium text-lg rounded-full text-white bg-green-400 hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          Save Settings
        </button>
      </Loading>
    </div>
  </div>
</form>
